import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, ArrowLeft, Pencil } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import { formatTextWithLinks } from '../../../utils/formatters';
import { Meeting } from '../../../types/meeting';
import AddMeetingModal from './components/AddMeetingModal';
import EditMeetingModal from './components/EditMeetingModal';

export default function MeetingsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  const { data: meetings, isLoading } = useQuery<Meeting[]>({
    queryKey: ['admin', 'comm_meetings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comm_meetings')
        .select('*')
        .order('meet_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data;
    }
  });

  const filteredMeetings = meetings?.filter(meeting => 
    meeting.meet_comm?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (meeting.meet_minutes?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'comm_meetings'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Meetings</h1>
            <p className="mt-1 text-sm text-gray-500">
              Add, edit, and manage community meeting records
            </p>
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Meeting
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center flex-1">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Community
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Notes and Docs
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredMeetings?.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No meeting records found
                  </td>
                </tr>
              ) : (
                filteredMeetings?.map((meeting) => (
                  <tr key={`${meeting.meet_comm}-${meeting.meet_date}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => {
                          setSelectedMeeting(meeting);
                          setIsEditModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {meeting.meet_comm}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {meeting.meet_date ? formatDate(meeting.meet_date) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="whitespace-normal break-words">
                        {formatTextWithLinks(meeting.meet_minutes)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      <AddMeetingModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedMeeting && (
        <EditMeetingModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedMeeting(null);
          }}
          onSuccess={handleSuccess}
          meeting={selectedMeeting}
        />
      )}
    </div>
  );
}
